import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Content, ContentStatus, ContentFormData } from '../types';
import { apiService } from '../services/api';
import { useDarfModerieren } from '../hooks/useDarfModerieren';
import { fälltAufEntwurf, RÜCKFALL_RÜCKFRAGE_BEITRAG } from '../utils/moderation';
import { Begründungsfrage } from '../components/ui/Begruendungsfrage';
import { WebComponentForm } from '../components/forms/WebComponentForm';
import { Versionsliste } from '../components/content/Versionsliste';
import { useRealtimeTable } from '../lib/useRealtimeTable';
import { getErrorData, getErrorDetails, getErrorMessage, getValidationErrorMessage } from '../utils/errors';
import { StatusBadge } from '../components/ui/StatusBadge';
import { PageLayout } from '../components/ui/PageLayout';
import { Benachrichtigung } from '../components/ui/Benachrichtigung';
import { useBenachrichtigung } from '../hooks/useBenachrichtigung';
import { Rueckfrage } from '../components/ui/Rueckfrage';
import { useRueckfrage } from '../hooks/useRueckfrage';
import { Filterleiste } from '../components/ui/Filterleiste';
import { SeitenblaetterFuss } from '../components/ui/SeitenblaetterFuss';
import { seitenwechsel, statusAuswahl } from '../components/ui/filterZustand';
import { datumKurz } from '../utils/datum';
import { bereichsFilter, LEER_MIT_FILTER, type BereichsEditor, type BereichsFilter, type ContentBereich } from './contentBereiche';

interface ContentManagementProps {
  /**
   * Welchen Bereich diese Seite zeigt. Pflicht — es gibt keine „alle Inhalte"-Ansicht mehr, und
   * genau daran krankte der Vorgängerstand: zwei Routen, dieselbe ungefilterte Liste.
   * Die Vorgaben stehen in `contentBereiche.ts`, verdrahtet wird in `App.tsx`.
   */
  bereich: ContentBereich;
}

const ContentManagement: React.FC<ContentManagementProps> = ({ bereich }) => {
  const [content, setContent] = useState<Content[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingContent, setEditingContent] = useState<Content | null>(null);
  // Der Typ ist KEIN Filter mehr, sondern die Identität der Seite — deshalb `BereichsFilter`
  // (= ContentFilters ohne `type`). Die Typ-Auswahlliste ist damit weg: sie hätte auf
  // „Blog-Beiträge" wieder Affirmationen zeigen können.
  const [filters, setFilters] = useState<BereichsFilter>({
    page: 1,
    limit: 10,
    search: '',
    status: undefined,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  // ⚠️ Diese Seite hatte als einzige der sechs Verwaltungsseiten GAR KEINEN Balken — sie meldete
  // den gescheiterten Speicherversuch mit einem nativen `alert()`. Das ist keine Fehleranzeige:
  // der Browser-Dialog blockiert den Faden, lässt sich nicht gestalten, meldet sich keiner
  // Vorlesehilfe als Live-Region an und trägt die Fehlermeldung aus der Seite heraus in ein
  // Fenster, dessen Aussehen die Anwendung nicht kennt. Zustand, Zeitmessung und Aufräumen
  // stecken jetzt im geteilten Haken; der Fehler bleibt stehen, statt nach drei Sekunden zu
  // verschwinden — Begründung in `Benachrichtigung.tsx`.
  const { inhalt: benachrichtigung, melde, schließe } = useBenachrichtigung();
  // ⚠️ `rückfrage`, nicht `inhalt` — der Name eine Zeile höher ist schon vergeben. Genau dafür
  // heißt das Feld im Haken so; Begründung in `useRueckfrage.ts`.
  const { rückfrage, frage, antworte } = useRueckfrage();
  // Wer freigeben und ablehnen darf — dieselbe Weiche wie in den Formularen, Begründung in
  // `utils/moderation.ts`. Kein Schutz: `/approve` und `/reject` antworten der Redaktion mit 403,
  // ganz gleich, was diese Seite anzeigt; ohne die Weiche sähe sie zwei Knöpfe, die ihr der Server
  // jedes Mal abweist.
  const darfPrüfen = useDarfModerieren();
  // Der Eintrag, dessen Ablehnung gerade begründet wird — oder `null`. Bewusst der ganze Eintrag
  // und nicht bloß seine Kennung: der Dialog nennt den Titel, und den trägt die Zeile, nicht die ID.
  const [abzulehnen, setAbzulehnen] = useState<Content | null>(null);

  // Phase 3 WP5.1 — live content list. Realtime delivers content INSERT/UPDATE/DELETE
  // (RLS-filtered to what this CMS user may see) → silently re-fetch so the list stays
  // consistent with the current filters/pagination, without a reload or polling.
  useRealtimeTable('content', () => {
    fetchContent({ silent: true });
  });

  // Memoisiert, damit der Effekt sie in seine Abhängigkeiten nehmen kann. Identität wechselt
  // genau dann, wenn sich `filters` ändert — also exakt das bisherige Verhalten von `[filters]`.
  const fetchContent = useCallback(async (opts?: { silent?: boolean }) => {
    const showLoading = !opts?.silent;
    try {
      if (showLoading) setIsLoading(true);
      console.log('🔄 Fetching content from API...');

      // Serverseitig auf den Bereichs-Typ eingegrenzt — nicht erst alles holen und im Browser
      // wegwerfen. Sonst zählte die Blätter-Leiste fremde Zeilen mit.
      const response = await apiService.getContent(bereichsFilter(bereich, filters));
      console.log('📦 API Response:', response);

      // apiService.getContent() gibt bereits { data: [...], pagination: {...} } zurück
      setContent(response.data || []);
      setPagination(response.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 });
      console.log('✅ Content fetched from API');
    } catch (error) {
      console.error('❌ Failed to fetch content from API:', error);
      // ⚠️ Ohne Meldung sah ein Ladefehler wie eine leere Liste aus — die Seite zeigte
      // „Keine Inhalte", und niemand konnte den Unterschied zwischen „nichts da" und
      // „Abruf gescheitert" sehen.
      melde('error', `Inhalte konnten nicht geladen werden: ${getErrorMessage(error)}`);
      setContent([]);
      setPagination({ page: 1, limit: 10, total: 0, totalPages: 0 });
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, [bereich, filters, melde]);

  // Steht bewusst NACH der memoisierten Funktion: seit sie in den Abhängigkeiten steht,
  // wird die Konstante schon beim Rendern gelesen — davor wäre das die temporale Todeszone.
  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleFilterChange = <K extends keyof BereichsFilter>(key: K, value: BereichsFilter[K]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  // ⚠️ Nimmt den ganzen Eintrag statt nur seiner Kennung, und das ist der Grund für die geänderte
  // Signatur: die alte Frage lautete „Diesen Eintrag wirklich löschen?" und nannte nicht, WELCHEN.
  // In einer Liste aus zehn ähnlich betitelten Zeilen ist das keine Rückfrage, sondern ein
  // Reflextest — der Titel steht in der Zeile, die Kennung nicht.
  //
  // Was der Text behauptet, ist am Backend nachgemessen: `ContentService.deleteContent` ruft
  // `tx.content.delete` — ein echtes DELETE, kein Stillegen eines Kennzeichens. `content_saves`
  // und `content_favorites` hängen per FK `onDelete: Cascade` daran und gehen mit; eine
  // DELETE-Fassung wird zwar als Revision mitgeschrieben, aber das CMS bietet keinen Weg zurück.
  // Deshalb steht dort „über diese Seite" und nicht „gar nicht" — behauptet wird nur, was gilt.
  const handleDelete = async (item: Content) => {
    if (
      !(await frage({
        titel: 'Eintrag löschen?',
        text:
          `„${item.title}" wird endgültig aus ${bereich.titel} entfernt. Gemerkte Einträge und ` +
          'Favoriten der Nutzerinnen dazu verschwinden mit.\n\n' +
          'Über diese Seite lässt sich der Schritt nicht zurücknehmen.',
        bestätigenText: 'Endgültig löschen',
        gefahr: true,
      }))
    ) {
      return;
    }
    try {
      await apiService.deleteContent(item.id);
      fetchContent();
    } catch (error) {
      console.error('Failed to delete content:', error);
      melde('error', `Löschen fehlgeschlagen: ${getErrorMessage(error)}`);
    }
  };

  const handlePublish = async (id: string) => {
    try {
      await apiService.publishContent(id);
      fetchContent();
    } catch (error) {
      console.error('Failed to publish content:', error);
      melde('error', `Veröffentlichen fehlgeschlagen: ${getErrorMessage(error)}`);
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await apiService.archiveContent(id);
      fetchContent();
    } catch (error) {
      console.error('Failed to archive content:', error);
      melde('error', `Archivieren fehlgeschlagen: ${getErrorMessage(error)}`);
    }
  };

  // Freigeben — ohne Rückfrage, genau wie `handlePublish` und `handleArchive` daneben. Der Schritt
  // ist über „Archivieren" zurücknehmbar, und eine Frage vor jeder folgenlos umkehrbaren Aktion
  // ist der Klick, den man wegklickt, ohne ihn zu lesen (Begründung an `gefahr` in `Rueckfrage.tsx`).
  const handleApprove = async (id: string) => {
    try {
      await apiService.approveContent(id);
      fetchContent();
    } catch (error) {
      console.error('Failed to approve content:', error);
      melde('error', `Freigeben fehlgeschlagen: ${getErrorMessage(error)}`);
    }
  };

  // Ablehnen — hier MIT Dialog, denn ohne Begründung wäre die Ablehnung eine Sackgasse: der Status
  // stünde in der Liste, der Grund nirgends. Die Pflicht sitzt in `Begründungsfrage`; hier kommt
  // deshalb nur an, was schon getrimmt und nicht leer ist.
  const handleReject = async (item: Content, begründung: string) => {
    setAbzulehnen(null);
    try {
      await apiService.rejectContent(item.id, begründung);
      fetchContent();
    } catch (error) {
      console.error('Failed to reject content:', error);
      melde('error', `Ablehnen fehlgeschlagen: ${getErrorMessage(error)}`);
    }
  };

  // Das Symbol ist jetzt eine Eigenschaft des Bereichs, keine Fallunterscheidung mehr: Die Liste
  // trägt genau einen Typ, also hatte die alte `switch`-Kaskade nur noch einen erreichbaren Fall.
  const BereichsIcon = bereich.Icon;

  // Kein Typ-Auswahl-Dialog mehr. Er fragte „Was möchten Sie anlegen?" und bot neben dem
  // Blog-Formular Verweise auf Audio-/Video-/MindBook-Verwaltung an — sinnvoll für eine Sammel-
  // seite, verwirrend auf einer Seite, die genau einen Typ führt. Die drei Verwaltungen stehen
  // ohnehin in der Seitenleiste.
  const handleCreateContent = () => {
    setEditingContent(null);
    setShowForm(true);
  };

  // Eingehend ist die Formular-Sicht (ContentFormData), ausgehend die Backend-Form
  // (ContentWritePayload: `type` in GROSSBUCHSTABEN, dazu slug/locale/contentData).
  // Dass die beiden verschieden sind, war vorher unsichtbar: die Nutzlast lief als `any`
  // in Service-Methoden, die fälschlich ContentFormData deklarierten.
  const handleFormSubmit = async (formData: ContentFormData) => {
    // F3: speichert die Redaktion ein veröffentlichtes Werk, fällt es auf den Entwurf. Das Formular
    // dieser Seite hat kein Statusfeld und schickt keinen Status — es gilt also immer der Rückfall,
    // nie ein bewusstes Depublizieren. Deshalb fragt die Seite, nicht das Formular.
    if (editingContent && fälltAufEntwurf(darfPrüfen, editingContent.status, undefined)
      && !(await frage(RÜCKFALL_RÜCKFRAGE_BEITRAG))) return;
    try {
      // Transformiere die Daten für das Backend
      // Neue Felder werden in metadata gespeichert, bis das Backend aktualisiert ist
      const transformedData = {
        title: formData.title,
        description: formData.description || '',
        // Der Typ kommt aus dem Bereich, nicht aus dem Formular: die Seite entscheidet, was sie
        // schreibt. `WebComponentForm` trug ihn bisher als eigenen Standardwert mit sich — eine
        // zweite Wahrheit, die auf einer typgebundenen Seite nichts zu suchen hat.
        type: bereich.type,
        slug: formData.title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .substring(0, 200),
        category: formData.category || 'Aktuelles',
        tags: formData.tags || [],
        locale: 'de',
        language: formData.language || 'de',
        isPremium: formData.isPremium ?? true, // Premium ueberall (Tom 2026-09-11)
        content: formData.content || '', // Stelle sicher, dass content immer ein String ist
        // `contentData.blog` gehört zum Web-Komponenten-Formular — dem einzigen, das diese Seite
        // besitzt, und es hängt am einzigen Bereich mit `editor` (Blog). Bekommt ein weiterer
        // Bereich ein Formular, gehört die Hülle mit an den Bereich; das Backend erwartet je Typ
        // eine eigene (`contentData.affirmation.text` bei AFFIRMATION, ContentValidation.ts).
        contentData: {
          blog: {
            content: formData.content || '', // Stelle sicher, dass content immer ein String ist
            heroImage: formData.metadata?.heroImage || '',
            subtitle: formData.metadata?.subtitle || '',
            teaserText: formData.metadata?.teaserText || ''
          }
        },
        metadata: {
          ...(formData.metadata || {}),
          // Neue Felder in metadata speichern
          shortDescription: formData.shortDescription || '',
          duration: formData.duration || '',
          headphones: formData.headphones || null,
          keywords: formData.keywords || '',
          topicCategories: formData.topicCategories || []
        }
      };

      if (editingContent) {
        // Update existing content
        console.log('📤 Updating content:', editingContent.id, transformedData);
        await apiService.updateContent(editingContent.id, transformedData);
        console.log('✅ Content updated successfully');
      } else {
        // Create new content
        console.log('📤 Creating new content:', transformedData);
        await apiService.createContent(transformedData);
        console.log('✅ Content created successfully');
      }
      
      setShowForm(false);
      setEditingContent(null);
      fetchContent();
    } catch (error: unknown) {
      console.error(`❌ Failed to ${editingContent ? 'update' : 'create'} content:`, error);
      console.error('❌ Error details:', getErrorDetails(error));

      const fallback = `Fehler beim ${editingContent ? 'Aktualisieren' : 'Erstellen'} des Contents. Bitte versuchen Sie es erneut.`;

      // Reihenfolge wie bisher: Validierungsfehler → Meldung aus dem Antwort-Body →
      // (nur wenn der Server gar nicht geantwortet hat) Error.message → Fallback.
      const data = getErrorData(error);
      const validationErrors = getValidationErrorMessage(error);

      let errorMessage: string;
      if (validationErrors) {
        // ⚠️ `getValidationErrorMessage` fügt die Einzelfehler mit `\n` zusammen. Im `alert()`
        // waren das echte Zeilen; im Balken ist der Umbruch nur ein Leerzeichen (HTML faltet
        // Weißraum), die Fehler klebten also unbemerkt aneinander. Deshalb ein sichtbares
        // Trennzeichen statt eines Umbruchs, den niemand sieht — und ausdrücklich nicht
        // `whitespace-pre-line` am geteilten Baustein: dessen Fläche gilt für alle sechs Seiten,
        // eine Seite darf sie nicht für ihren Sonderfall umbauen.
        errorMessage = `Validierungsfehler: ${validationErrors.split('\n').filter(Boolean).join(' · ')}`;
      } else if (data) {
        errorMessage = data.message || data.error || fallback;
      } else {
        errorMessage = getErrorMessage(error, fallback);
      }

      // Das Formular bleibt hier bewusst OFFEN — `setShowForm(false)` steht im `try`, damit die
      // Eingaben der Nutzerin nicht verloren gehen. Genau deshalb steht der Balken ÜBER der
      // Weiche und nicht in einem ihrer Zweige; Begründung am Balken unten.
      melde('error', `Fehler beim ${editingContent ? 'Aktualisieren' : 'Erstellen'} des Contents: ${errorMessage}`);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingContent(null);
  };

  // Nach dem Wiederherstellen einer Fassung.
  //
  // ⚠️ Das Formular wird GESCHLOSSEN, und das ist keine Bequemlichkeit. `WebComponentForm` liest
  // `initialData` genau einmal (`useState(() => …)` im Konstruktor-Rendern, kein Abgleich per
  // Effekt — nachgemessen). Bliebe das Formular stehen, zeigte es weiterhin den Stand VOR der
  // Wiederherstellung; ein Klick auf „Speichern" schriebe genau den zurück und machte den Vorgang
  // stillschweigend rückgängig. Die Nutzerin sähe dabei nichts Auffälliges.
  //
  // Die Erfolgsmeldung setzt die Versionsliste selbst, BEVOR sie hier abgebaut wird — der Balken
  // hängt über der Weiche und überlebt den Wechsel (siehe unten).
  const handleWiederhergestellt = () => {
    setShowForm(false);
    setEditingContent(null);
    fetchContent();
  };

  // Kein Typ-Zweig mehr: Die Liste führt genau den Typ des Bereichs, und der Knopf erscheint nur,
  // wo es ein Formular gibt (siehe unten). Der frühere Zweig „anderer Typ → Weiterleitung oder
  // Hinweis" war die Folge der gemeinsamen, ungefilterten Liste und ist mit ihr weg.
  const handleEdit = (item: Content) => {
    setEditingContent(item);
    setShowForm(true);
  };

  // DIE Weiche dieser Seite, einmal berechnet statt zweimal geschrieben. `undefined` heißt
  // „Listen-Zweig": entweder ist kein Formular offen, oder der Bereich hat gar keines
  // (Affirmationen). Als Wert statt als Bedingung, weil daran drei Dinge hängen, die sonst
  // auseinanderdriften könnten — Titel, Aktionsknopf und die Frage, ob der Balken schwebt.
  const offenerEditor: BereichsEditor | undefined = showForm ? bereich.editor : undefined;

  // Nimmt den Editor NICHT als Parameter: seine Texte stehen inzwischen alle in der Titelzeile der
  // Vorlage, hier drin bleibt nur das Formular selbst. Dass dieser Zweig ausschließlich mit einem
  // Editor erreichbar ist, sichert `offenerEditor` an der Aufrufstelle.
  const formularZweig = () => {
    // Bereite initialData für das Formular vor
    // Neue Felder werden aus metadata geladen, falls sie dort gespeichert sind
    const initialData = editingContent ? {
      title: editingContent.title,
      description: editingContent.description || '',
      shortDescription: editingContent.shortDescription || editingContent.metadata?.shortDescription || '',
      duration: String(editingContent.duration || editingContent.metadata?.duration || ''),
      // Schlusswert `undefined` statt `null`: WebComponentForm liest das Feld ausschließlich als
      // `initialData.headphones || HeadphonesRecommendation.NO`, beide Werte landen also auf
      // demselben Default. `null` passt nicht in `Partial<ContentFormData>` und war nur deshalb
      // unbemerkt, weil `metadata.headphones` vorher über die any-Index-Signatur lief.
      headphones: editingContent.headphones || editingContent.metadata?.headphones || undefined,
      keywords: editingContent.keywords || editingContent.metadata?.keywords || '',
      category: editingContent.category || '',
      topicCategories: editingContent.topicCategories || editingContent.metadata?.topicCategories || [],
      tags: editingContent.tags || [],
      content: editingContent.contentData?.blog?.content ||
               editingContent.content ||
               '',
      metadata: {
        subtitle: editingContent.contentData?.blog?.subtitle ||
                 editingContent.metadata?.subtitle || '',
        heroImage: editingContent.contentData?.blog?.heroImage ||
                  editingContent.metadata?.heroImage || '',
        teaserText: editingContent.contentData?.blog?.teaserText ||
                   editingContent.metadata?.teaserText || ''
      },
      isPremium: editingContent.isPremium ?? true,
      language: editingContent.language || 'de'
    } : undefined;

    return (
      <>
        <div className="card">
          <div className="card-content">
            <WebComponentForm
              initialData={initialData}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isLoading={false}
              bestandStatus={editingContent?.status}
            />
          </div>
        </div>

        {/* Die Fassungen dieses Inhalts — UNTER dem Formular und nur hier.
            ⚠️ `editingContent &&` ist keine Vorsichtsmaßnahme, sondern die Bedingung: Fassungen
            hängen an einer Inhalts-Kennung, und die gibt es erst, wenn ein Inhalt existiert. Beim
            Anlegen (`editingContent === null`) wäre die Liste zwangsläufig leer und die Frage
            „welchen Stand hole ich zurück?" sinnlos.
            ⚠️ `frage` und `melde` werden durchgereicht statt im Bauteil neu geholt: Dialog und
            Balken der Seite stehen ÜBER der Weiche, damit ihre Meldung den Zweigwechsel überlebt.
            Ein eigenes Paar im Bauteil verschwände mitsamt dem Bauteil — und genau das passiert
            beim Wiederherstellen (siehe `handleWiederhergestellt`). */}
        {editingContent && (
          <Versionsliste
            contentId={editingContent.id}
            contentTitel={editingContent.title}
            frage={frage}
            melde={melde}
            onWiederhergestellt={handleWiederhergestellt}
          />
        )}
      </>
    );
  };

  const listenZweig = () => (
    <>
      {/* ⚠️ Die Typ-Auswahl stand hier bis 2026-08-12 („Alle Typen" plus alle sechs Enum-Werte).
          Sie ist mit der Bereichs-Trennung weg: auf einer Seite „Blog-Beiträge" hätte man damit
          Affirmationen einblenden können — also genau den Zustand wiederherstellen, den dieser
          Umbau beseitigt. Der Typ steht jetzt in `bereich.type` und wandert serverseitig mit.
          Ebenfalls entfernt: der Knopf „Filter anwenden". Er trug kein `onClick` und lag in
          keinem Formular, war also wirkungslos — die Filter greifen ohnehin bei jeder Änderung.

          ⚠️ Hier stand bis zuletzt eine HANDKOPIE der Filterleiste — dieselbe Klassenkette wie bei
          den vier Nachbarseiten, nur mit eigener Drift (Lupe `w-4 h-4` statt `w-5 h-5`, leere
          Option „Alle Status" statt „Alle", feste `id="inhalte-suche"` statt `useId`). Sie wurde
          beim Zusammenlegen übersehen, obwohl sie dabei sogar angefasst wurde. Die Drift ist mit
          dem Umstieg aufgelöst; die Begründung je Punkt steht im Kopf von `Filterleiste.tsx`. */}
      <Filterleiste
        suche={filters.search || ''}
        onSuche={(wert) => handleFilterChange('search', wert)}
        platzhalter="Titel, Beschreibung…"
        auswahlen={[
          // ⚠️ Hier stand eine HANDKOPIE von `statusAuswahl` mit der Begründung, das geteilte
          // Feld sei die Dreier-Fassung von Audio/Video/MindBooks und verschlucke `REJECTED`.
          // Die Begründung stimmte — und ist mit den drei Enums verschwunden: alle vier Seiten
          // führen jetzt `ContentStatus`, `statusAuswahl` bietet alle Stufen an. Damit ist diese
          // Seite ein Kopierfall wie die anderen drei. Sichtbare Folge: die Reihenfolge ist jetzt
          // die der Nachbarseiten (veröffentlicht zuerst) statt der Deklarations-Reihenfolge des
          // Enums — vier gleiche Listen sind mehr wert als vier eigene Reihenfolgen.
          statusAuswahl(filters.status, (wert) => handleFilterChange('status', wert)),
        ]}
      />

      {/* Content Table */}
      <div className="card">
        <div className="card-content p-0">
          {isLoading ? (
            <div className="p-6">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="skeleton h-16 w-full"></div>
                ))}
              </div>
            </div>
          ) : content.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  {/* Die Spalte „Typ" ist weg: sie trüge auf jeder Zeile denselben Wert wie die
                      Überschrift der Seite. */}
                  <tr>
                    <th>Inhalt</th>
                    <th>Status</th>
                    <th>Autor</th>
                    <th>Erstellt</th>
                    <th>Aufrufe</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {content.map((item) => (
                    <tr key={item.id}>
                      {/* Der Titel ist der Weg ins Formular — Hausform der sieben Verwaltungs-Listen
                          (`AudioManagement` u. a.). Vorher führte nur der Stift in „Aktionen" hinein, und
                          der lag unter rund 1210 px Fensterbreite außerhalb des Bildes, ohne dass etwas
                          das Scrollen anzeigte (BACKLOG `cms-listen-zu-breit-aktionen-ausserhalb`).
                          `whitespace-normal` hebt das geerbte `nowrap` aus `.table td` auf: Titel und
                          Beschreibung brechen um, statt die Tabelle zu verbreitern.
                          ⚠️ Am inneren `div`, NICHT an der `td`: dort verlöre es gegen `.table td`
                          (Spezifität 0,1,1 gegen 0,1,0 — `@layer components` ist in Tailwind 3 keine
                          echte CSS-Ebene). Die eigene Angabe des `div` schlägt dagegen jede geerbte.
                          `min-w-[16rem]`: die übrigen fünf Spalten brechen nicht um und geben keinen Pixel
                          ab — ohne Untergrenze bekam der Titel bei 1024 px nur 71 px und stand auf fünf
                          Zeilen (gemessen auf dev 2026-10-05). Mit 16rem scrollt die Tabelle dort in der
                          Karte wie die übrigen Listen; bei 1280 px ändert sich nichts (⭐️-Entscheid). Gilt für
                          beide Listen dieser Seite, Blog und Affirmationen (`App.tsx`: `content/blog`, `content/affirmations`). */}
                      <td>
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <BereichsIcon className={`w-5 h-5 ${bereich.iconKlasse}`} />
                          </div>
                          <div className="min-w-[16rem] whitespace-normal">
                            {bereich.editor ? (
                              <button
                                type="button"
                                onClick={() => handleEdit(item)}
                                className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                              >
                                {item.title}
                              </button>
                            ) : (
                              <p className="text-sm font-medium text-secondary-900">{item.title}</p>
                            )}
                            <p className="text-sm text-secondary-500 line-clamp-2">
                              {item.description}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={item.status} />
                      </td>
                      <td>
                        <div className="text-sm text-secondary-900">
                          {item.author?.username || 'Unknown'}
                        </div>
                      </td>
                      <td>
                        <div className="text-sm text-secondary-500">
                          {datumKurz(item.createdAt)}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center space-x-1">
                          <EyeIcon className="w-4 h-4 text-secondary-400" />
                          <span className="text-sm text-secondary-600">
                            {item.playCount || 0}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          {/* ⚠️ Hier stand als erster Knopf ein Auge mit `onClick={() => {}}` und
                              einem leeren Kommentar „View content" — sichtbar, anklickbar,
                              wirkungslos. Entfernt.
                              „Bearbeiten" erscheint nur, wo der Bereich ein Formular hat; sonst
                              führte der Stift bisher in ein `alert`. */}
                          {bereich.editor && (
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-1 text-secondary-400 hover:text-secondary-600"
                              title="Bearbeiten"
                              aria-label="Bearbeiten"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                          )}
                          {/* Veröffentlichen, Archivieren und Löschen sind Moderation:
                              `POST /api/content/:id/publish` und `/archive` sowie `DELETE /:id`
                              stehen hinter `authorize(['ADMIN', 'MODERATOR'])` (aus den Routen
                              gelesen). Auf dev gemessen ist „Archivieren“: die Redaktion bekam
                              „Archivieren fehlgeschlagen: Unzureichende Berechtigung“ (2026-10-02,
                              BACKLOG `cms-listenknoepfe-redaktion`). */}
                          {darfPrüfen && item.status === ContentStatus.DRAFT && (
                            <button
                              onClick={() => handlePublish(item.id)}
                              className="p-1 text-success-400 hover:text-success-600"
                              title="Veröffentlichen"
                              aria-label="Veröffentlichen"
                            >
                              <PlayIcon className="w-4 h-4" />
                            </button>
                          )}
                          {darfPrüfen && item.status === ContentStatus.PUBLISHED && (
                            <button
                              onClick={() => handleArchive(item.id)}
                              className="p-1 text-warning-400 hover:text-warning-600"
                              title="Archivieren"
                              aria-label="Archivieren"
                            >
                              <DocumentTextIcon className="w-4 h-4" />
                            </button>
                          )}
                          {/* ── Die Prüfung: ablehnen, und aus der Ablehnung wieder heraus ──────
                              ⚠️ Warum „Ablehnen" am Entwurf hängt und „Freigeben" NICHT: am
                              Backend nachgemessen, nicht angenommen. `POST /api/content/:id/publish`
                              (der grüne Knopf darüber) und `POST /api/admin/content/:id/approve`
                              landen BEIDE in `ContentService.publishContent` — dieselbe Mutation,
                              zwei Türen. Ein „Freigeben" neben „Veröffentlichen" wäre also der
                              zweite Knopf für denselben Vorgang, und genau dieses Muster („zwei
                              Menüpunkte, dieselbe Liste") hat diese Seite gerade erst verloren.

                              Was WIRKLICH fehlte, sind die beiden Wege, die es sonst nirgends
                              gibt: aus einem Entwurf heraus ablehnen, und einen abgelehnten
                              Eintrag wieder freigeben. Der Statusfilter bot „Abgelehnt" bisher an,
                              ohne dass irgendein Datensatz den Wert je tragen konnte — die
                              Nutzerin las das als „es gibt keine abgelehnten Inhalte". Erst diese
                              beiden Knöpfe machen die Stufe wahr UND wieder verlassbar; eine
                              Ablehnung ohne Rückweg wäre eine Einbahnstraße. */}
                          {darfPrüfen && item.status === ContentStatus.DRAFT && (
                            <button
                              onClick={() => setAbzulehnen(item)}
                              className="p-1 text-error-400 hover:text-error-600 cursor-pointer"
                              title="Ablehnen"
                              aria-label="Ablehnen"
                            >
                              <XCircleIcon className="w-4 h-4" />
                            </button>
                          )}
                          {darfPrüfen && item.status === ContentStatus.REJECTED && (
                            <button
                              onClick={() => handleApprove(item.id)}
                              className="p-1 text-success-400 hover:text-success-600 cursor-pointer"
                              title="Freigeben"
                              aria-label="Freigeben"
                            >
                              <CheckCircleIcon className="w-4 h-4" />
                            </button>
                          )}
                          {darfPrüfen && (
                            <button
                              onClick={() => void handleDelete(item)}
                              className="p-1 text-error-400 hover:text-error-600"
                              title="Löschen"
                              aria-label="Löschen"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <BereichsIcon className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 mb-2">
                {filters.search || filters.status ? 'Keine Treffer' : bereich.leerTitel}
              </h3>
              <p className="mx-auto mb-4 max-w-prose text-secondary-500">
                {filters.search || filters.status ? LEER_MIT_FILTER : bereich.leerText}
              </p>
              {/* ⚠️ Hier stand ein zweiter „Inhalt anlegen"-Knopf ganz ohne `onClick` — der
                  einzige Weg ins Formular, den die leere Liste anbot, war also keiner. Jetzt
                  derselbe Handler wie oben, und nur dort, wo es ein Formular gibt. */}
              {bereich.editor && !filters.search && !filters.status && (
                <button onClick={handleCreateContent} className="btn btn-primary">
                  <PlusIcon className="w-4 h-4 mr-2" />
                  {bereich.editor.anlegenKnopf}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Seitenblättern — angeglichen an die Mehrheitsfassung der übrigen vier Listen.
            ⚠️ Die Knöpfe hingen bis hierher an `handleFilterChange('page', …)`, und das setzte im
            selben Objekt `page: 1` HINTER den übergebenen Wert: „Weiter" lud damit wieder Seite 1,
            die Leiste war also wirkungslos. `seitenwechsel` hält genau diese Regel fest — die
            Seitenzahl ist kein Filter, der die Liste an den Anfang zurückholen darf. */}
        <SeitenblaetterFuss
          seite={pagination.page}
          seitenAnzahl={pagination.totalPages}
          gesamt={pagination.total}
          onSeitenwechsel={seitenwechsel(setFilters)}
        />
      </div>
    </>
  );

  return (
    <PageLayout
      title={
        offenerEditor
          ? (editingContent ? offenerEditor.titelBearbeiten : offenerEditor.titelNeu)
          : bereich.titel
      }
      subtitle={
        offenerEditor
          ? (editingContent ? offenerEditor.untertitelBearbeiten : offenerEditor.untertitelNeu)
          : bereich.untertitel
      }
      actions={
        offenerEditor ? (
          <button
            onClick={handleFormCancel}
            className="btn btn-outline"
          >
            Zurück zur Übersicht
          </button>
        ) : (
          // Nur, wo es wirklich ein Formular gibt. Ein „Anlegen"-Knopf ohne Maske wäre dieselbe
          // Attrappe wie die beiden Menüpunkte, die auf dieselbe Liste zeigten.
          bereich.editor && (
            <button
              onClick={handleCreateContent}
              className="btn btn-primary"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              {bereich.editor.anlegenKnopf}
            </button>
          )
        )
      }
    >
      {/* ⚠️ EIN Balken, und zwar VOR der Weiche. Diese Seite hatte bis hierher überhaupt keinen:
          `handleFormSubmit` meldete im `catch` mit einem nativen `alert()`. Das ist ein anderes
          Symptom derselben Wurzel wie bei Audio, Video und MindBooks — die Meldung entsteht in
          einem Zustand, für den die Seite keine Anzeige hat, und behilft sich mit einem
          Browser-Dialog. Ein `alert()` blockiert den Faden, lässt sich nicht gestalten, kennt die
          Farben der Anwendung nicht und meldet sich keiner Vorlesehilfe als Live-Region an. Das
          Formular bleibt im Fehlerfall bewusst OFFEN (`setShowForm(false)` steht im `try`), damit
          die Eingaben nicht verloren gehen — der Balken muss diesen Zustand also überleben, und
          das tut er nur oberhalb der Weiche.

          ⚠️ Gemessen statt angenommen, wie bei den Geschwisterseiten: `WebComponentForm` hält
          KEINE eigene Meldungs-Quelle (kein `useBenachrichtigung`, kein `<Benachrichtigung>`,
          keine `notify`-Eigenschaft), es gibt hier also nichts zusammenzulegen und keine zwei
          schwebenden Balken, die sich überlagern könnten. `schließe` wird auf dieser Seite an
          genau einer Stelle benutzt — am Schließen-Knopf des Balkens hier. Kein Aufruf räumt eine
          stehende Speicher-Fehlermeldung weg.

          `schwebend` nur im Formular-Zweig, und die Begründung ist hier eine ANDERE als bei
          Audio/Video/MindBooks: `WebComponentForm` hat KEINE festgestellte Knopfleiste — seine
          Knöpfe stehen im Fluss (`flex justify-end gap-4 pt-4 border-t`). Es öffnet aber denselben
          Datei-Wähler (`.bunny-file-picker-overlay`: `fixed inset-0 z-50` in `index.css`), und im
          Textfluss stünde der Balken hinter dessen Schleier und außerhalb des Sichtfelds — genau
          der Fall, den `schwebend` in `Benachrichtigung.tsx` begründet. `schwebend` bringt
          `fixed top-4 right-4 z-[60]` mit und schlägt die 50 des Wählers erklärt statt über die
          Dokumentreihenfolge; der Balken steht VOR dem Wähler im Baum, bei Gleichstand verlöre er
          also. Der Listen-Zweig hat kein Overlay und bleibt bei der Mehrheitsfassung im Fluss.

          ⚠️ Was diese Rechnung NICHT ist: eine Trefferprobe im laufenden Browser. Unter Vitest ist
          sie gar nicht messbar — jsdom hat kein Layout, `getBoundingClientRect` meldet Nullen. Die
          Tests nageln deshalb die deklarierten Stapel- und Positionswerte fest, nicht Pixel. */}
      <Benachrichtigung
        inhalt={benachrichtigung}
        onSchließen={schließe}
        schwebend={offenerEditor !== undefined}
      />

      {/* Die Rückfrage vor dem Löschen. Ihre Stelle im Baum ist für die Darstellung gleichgültig
          (`ModalSchicht` hängt sie per Portal an `document.body`), sie steht aber bewusst wie der
          Balken ÜBER der Weiche: der Löschen-Knopf sitzt im Listen-Zweig, und eine Frage, die an
          einem Zweig hinge, verschwände beim Wechsel mitsamt ihrem unerfüllten Versprechen.

          ⚠️ Sie liegt auf `z-[55]`, der schwebende Balken auf `z-[60]` — bewusst so herum: eine
          stehende Fehlermeldung bleibt neben der offenen Frage lesbar, statt hinter deren Schleier
          zu verschwinden. Begründung an `RÜCKFRAGE_STAPEL` in `Rueckfrage.tsx`. */}
      <Rueckfrage rückfrage={rückfrage} onAntwort={antworte} />

      {/* Die Begründung vor der Ablehnung. Steht aus demselben Grund über der Weiche wie die
          Rückfrage darüber — der auslösende Knopf sitzt im Listen-Zweig, und ein Dialog, der an
          einem Zweig hinge, verschwände beim Wechsel mitsamt der halb getippten Begründung.

          Der Titel wandert in den Text, nicht bloß die Kennung: in einer Liste ähnlich betitelter
          Zeilen ist „Diesen Eintrag ablehnen?" ein Reflextest, kein Dialog. Dieselbe Begründung
          wie bei `handleDelete`.

          ⚠️ Als Weiche auf `abzulehnen` und nicht dauerhaft montiert mit einer `offen`-Eigenschaft:
          so ist „offen" gleich „montiert", das Textfeld startet bei jeder Frage leer, und
          `useFokusfang` trägt Merken/Setzen/Zurückgeben des Fokus an Aufbau und Abbau. Eine
          stehen bleibende Begründung vom letzten Mal wäre der schlimmere Fehler: sie ginge als
          Grund für einen anderen Beitrag in die Datenbank. */}
      {abzulehnen && (
        <Begründungsfrage
          titel="Inhalt ablehnen?"
          text={
            `„${abzulehnen.title}" wird als abgelehnt gekennzeichnet und bleibt unveröffentlicht.\n\n` +
            'Die Begründung wird zum Eintrag gespeichert — sie ist das Einzige, woran später ' +
            'ablesbar ist, warum der Beitrag nicht erschienen ist.'
          }
          feldBeschriftung="Begründung"
          platzhalter="Was spricht gegen die Veröffentlichung?"
          bestätigenText="Ablehnen"
          onAbbrechen={() => setAbzulehnen(null)}
          onBestätigen={(begründung) => void handleReject(abzulehnen, begründung)}
        />
      )}

      {offenerEditor ? formularZweig() : listenZweig()}
    </PageLayout>
  );
};

export default ContentManagement;
